import { useSelector } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router'

import { ProtectedRoute } from '@webapp/app/components'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useFeatureFlag } from '@webapp/hooks'
import { useAppRoutes } from '@webapp/hooks/useAppRoutes'
import { IntegrationServicePlatforms } from '@webapp/models/enums'
import { APIApplicationPage } from '@webapp/pages/APIApplicationPage'
import { Dynamics365BusinessCentralPage } from '@webapp/pages/Dynamics365BusinessCentralPage'
import { IntegrationMergePage } from '@webapp/pages/IntegrationMergePage'
import { IntegrationNetsuitePage } from '@webapp/pages/IntegrationNetsuitePage'
import { IntegrationPlatformListPage } from '@webapp/pages/IntegrationPlatformListPage'
import { NetsuitePage } from '@webapp/pages/NetsuitePage'
import { QboPage } from '@webapp/pages/QboPage'
import { SageIntacctPage } from '@webapp/pages/SageIntacctPage'
import { useGetIntegrationServiceState } from '@webapp/platform/_unmigrated/hooks/useGetIntegrationServiceState'
import { Permissions } from '@webapp/platform/iam'
import { IntegrationNames } from '@webapp/platform/integrations/entities/enums/IntegrationNameEnum'
import { IntegrationDetailsPage } from '@webapp/platform/integrations/pages/IntegrationDetailsPage/IntegrationDetailsPage'
import {
  QuickBooksDesktopImportStatuses,
  QuickBooksDesktopPage,
  QuickBooksDesktopSettingsTabs,
} from '@webapp/platform/integrations/pages/QuickBooksDesktopPage'
import { SlackIntegrationPage } from '@webapp/platform/integrations/pages/SlackIntegrationPage'
import { getCurrentNetSuiteEnvironment } from '@webapp/utils/integration/getCurrentNetSuiteEnvironment'
import { getCurrentSageEnvironment } from '@webapp/utils/integration/getCurrentSageEnvironment'

const QUICKBOOKS_SETTINGS_TAB_PARAM =
  ':tab(general|import-export|activity-log|error-log)'
const QUICKBOOKS_FIELD_MAPPING_MODEL_PARAM = ':model(po|bills)'
const QUICKBOOKS_IMPORT_STATUS_PARAM = ':status(unimported|imported)'
const QUICKBOOKS_EXPORT_RESOURCE_PARAM =
  ':resource(purchase-orders|item-receipts|bills)'
const QUICKBOOKS_EXPORT_STATUS_PARAM = ':status(exportable|exported|archived)'

const IntegrationsRoute = ({ path }) => {
  return (
    <ProtectedRoute
      exact
      path={path}
      component={IntegrationPlatformListPage}
      permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
    />
  )
}

export const IntegrationsRouter = () => {
  const { url, brp } = useAppRoutes()

  const PSERV_346_BASE_MIGRATION = useFeatureFlag('PSERV_346_BASE_MIGRATION')

  const { api_application_setup: showApplicationSetup } = useSelector(
    makeSelectFeatureSwitches()
  )

  const PLAT_2155_MERGE_NETSUITE = useFeatureFlag('PLAT_2155_MERGE_NETSUITE')
  const PLAT_5164_MERGE_SAGE = useFeatureFlag('PLAT_5164_MERGE_SAGE')

  const { currentQboEnvironment, data } = useGetIntegrationServiceState()
  const hasMergeQboIntegration =
    currentQboEnvironment ===
    IntegrationServicePlatforms.MERGE_QUICKBOOKS_ONLINE

  const currentNetSuiteEnvironment = PLAT_2155_MERGE_NETSUITE
    ? getCurrentNetSuiteEnvironment(data)
    : IntegrationServicePlatforms.NETSUITE

  const hasMergeNetSuiteIntegration = PLAT_2155_MERGE_NETSUITE
    ? currentNetSuiteEnvironment === IntegrationServicePlatforms.MERGE_NETSUITE
    : false

  const currentSageEnvironment = PLAT_5164_MERGE_SAGE
    ? getCurrentSageEnvironment(data)
    : IntegrationServicePlatforms.SAGE_INTACCT

  const hasMergeSageIntegration = PLAT_5164_MERGE_SAGE
    ? currentSageEnvironment === IntegrationServicePlatforms.MERGE_SAGE_INTACCT
    : false

  return (
    <Switch>
      <Route
        exact
        path={brp('NetsuitePage')}
        component={IntegrationNetsuitePage}
      />

      <Route
        path={brp('NetsuiteBillsPage')}
        render={(props) => {
          return hasMergeNetSuiteIntegration ? (
            <IntegrationDetailsPage
              platform={IntegrationNames.MERGE_NETSUITE}
            />
          ) : (
            <NetsuitePage {...props} />
          )
        }}
      />

      <Route
        path={brp('QboPage')}
        component={hasMergeQboIntegration ? IntegrationMergePage : QboPage}
      />

      <Route
        path={brp('SageIntacctPage')}
        render={(props) => {
          return hasMergeSageIntegration ? (
            <IntegrationDetailsPage
              platform={IntegrationNames.MERGE_SAGE_INTACCT}
            />
          ) : (
            <SageIntacctPage {...props} />
          )
        }}
      />

      <Route
        path={brp('Dynamics365BCPage')}
        component={Dynamics365BusinessCentralPage}
      />

      {showApplicationSetup && (
        <ProtectedRoute
          path={brp('ProcurifyApiPage')}
          component={APIApplicationPage}
          navigationPermissions={['settings']}
        />
      )}

      <ProtectedRoute
        exact
        path={brp('SlackIntegrationPage')}
        component={SlackIntegrationPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
      />

      {/* PSERV_346: flag-off falls back to the Angular QuickBooks Desktop page. */}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from='/integrations/quickbooks'
          to={url('LegacyQuickBooksDesktopPage')}
        />
      )}

      <ProtectedRoute
        exact
        path={brp('QuickBooksDesktopSettingsPage', {
          tab: QUICKBOOKS_SETTINGS_TAB_PARAM,
        })}
        component={QuickBooksDesktopPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <ProtectedRoute
        exact
        path={brp('QuickBooksDesktopFieldMappingPage', {
          model: QUICKBOOKS_FIELD_MAPPING_MODEL_PARAM,
        })}
        component={QuickBooksDesktopPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <ProtectedRoute
        exact
        path={brp('QuickBooksDesktopAccountsPage', {
          status: QUICKBOOKS_IMPORT_STATUS_PARAM,
        })}
        component={QuickBooksDesktopPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <ProtectedRoute
        exact
        path={brp('QuickBooksDesktopVendorsPage', {
          status: QUICKBOOKS_IMPORT_STATUS_PARAM,
        })}
        component={QuickBooksDesktopPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <ProtectedRoute
        exact
        path={brp('QuickBooksDesktopExportsPage', {
          resource: QUICKBOOKS_EXPORT_RESOURCE_PARAM,
          status: QUICKBOOKS_EXPORT_STATUS_PARAM,
        })}
        component={QuickBooksDesktopPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />

      {/* Bare or unknown status segments fall back to each tab's default
          (registered AFTER the constrained routes). */}
      <Redirect
        exact
        from='/integrations/quickbooks'
        to={url('QuickBooksDesktopSettingsPage', {
          tab: QuickBooksDesktopSettingsTabs.GENERAL,
        })}
      />
      <Redirect
        from={brp('QuickBooksDesktopAccountsPage', { status: ':status?' })}
        to={url('QuickBooksDesktopAccountsPage', {
          status: QuickBooksDesktopImportStatuses.UNIMPORTED,
        })}
      />
      <Redirect
        from={brp('QuickBooksDesktopVendorsPage', { status: ':status?' })}
        to={url('QuickBooksDesktopVendorsPage', {
          status: QuickBooksDesktopImportStatuses.UNIMPORTED,
        })}
      />
      <Redirect
        from={brp('QuickBooksDesktopExportsPage', {
          resource: QUICKBOOKS_EXPORT_RESOURCE_PARAM,
          status: ':status?',
        })}
        to='/integrations/quickbooks/:resource/exportable'
      />

      <IntegrationsRoute path={brp('IntegrationsPage')} />
    </Switch>
  )
}
